import { cn } from "@/lib/cn";

/*
  Avatar — iniciales sobre un bloque de color cálido. NUNCA URLs de fotos externas
  (dirección de diseño obligatoria). El color se deriva determinísticamente de las
  iniciales/nombre para que la misma persona tenga siempre el mismo tono.
*/

type Size = "sm" | "md" | "lg" | "xl";

const sizes: Record<Size, string> = {
  sm: "h-8 w-8 text-xs",
  md: "h-11 w-11 text-sm",
  lg: "h-16 w-16 text-lg",
  xl: "h-24 w-24 text-2xl",
};

// Paleta cálida para fondos de avatar (arena/terracota/verde apagado).
const PALETTE = [
  { bg: "#e6c2ac", fg: "#7a4a2e" }, // terracota clara
  { bg: "#d8c9af", fg: "#5a4a2e" }, // arena
  { bg: "#b9ccc0", fg: "#26402f" }, // verde apagado
  { bg: "#e9d5b8", fg: "#7a5a24" }, // dorado suave
  { bg: "#dcc4b0", fg: "#6e3f2a" }, // barro
  { bg: "#c9d3c0", fg: "#3d4a30" }, // oliva claro
];

function pickColor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export interface AvatarProps {
  /** Iniciales ya calculadas (ej. "VM"). Si no, se derivan de `name`. */
  initials?: string;
  name?: string;
  size?: Size;
  className?: string;
}

function toInitials(name?: string) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function Avatar({ initials, name, size = "md", className }: AvatarProps) {
  const text = (initials ?? toInitials(name)).slice(0, 2);
  const { bg, fg } = pickColor(text + (name ?? ""));
  return (
    <span
      aria-hidden={!name}
      role={name ? "img" : undefined}
      aria-label={name}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-display font-medium select-none",
        sizes[size],
        className,
      )}
      style={{ backgroundColor: bg, color: fg }}
    >
      {text}
    </span>
  );
}
